import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Save, Info, ExternalLink } from "lucide-react";
import { apiClient, formatApiError } from "../../lib/api";
import { HistoryButton } from "./HistoryDialog";

const PAGES = [
    { key: "impressum", label: "Impressum", path: "/impressum" },
    { key: "datenschutz", label: "Datenschutzerklärung", path: "/datenschutz" },
];

export const LegalPages = () => {
    const [contents, setContents] = useState({});
    const [loading, setLoading] = useState(true);
    const [savingKey, setSavingKey] = useState(null);

    const fetchPages = useCallback(async () => {
        try {
            const results = await Promise.all(PAGES.map((p) => apiClient.get(`/pages/${p.key}`)));
            const map = {};
            results.forEach(({ data }) => {
                map[data.key] = data.content;
            });
            setContents(map);
        } catch {
            toast.error("Rechtstexte konnten nicht geladen werden.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPages();
    }, [fetchPages]);

    const savePage = async (key) => {
        if (!contents[key]?.trim()) {
            toast.error("Der Text darf nicht leer sein.");
            return;
        }
        setSavingKey(key);
        try {
            await apiClient.put(`/admin/pages/${key}`, { content: contents[key] });
            toast.success("Gespeichert – sofort auf der Website sichtbar.");
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Speichern fehlgeschlagen.");
        } finally {
            setSavingKey(null);
        }
    };

    return (
        <div data-testid="legal-pages" className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3 text-sm text-blue-900">
                <Info size={18} className="mt-0.5 shrink-0" />
                <div>
                    Formatierung: Zeilen mit <code className="bg-blue-100 px-1 rounded">## </code> am Anfang werden zu
                    Überschriften, Zeilen mit <code className="bg-blue-100 px-1 rounded">- </code> zu Aufzählungspunkten.
                    Absätze durch Leerzeilen trennen.
                </div>
            </div>

            {loading && (
                <div className="bg-white border border-moebus-border rounded-lg p-8 text-center text-slate-500">
                    Lade…
                </div>
            )}

            {!loading &&
                PAGES.map((p) => (
                    <div key={p.key} data-testid={`legal-${p.key}`} className="bg-white border border-moebus-border rounded-lg p-4 md:p-6">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                                <h2 className="font-heading font-semibold text-lg text-moebus-deep">{p.label}</h2>
                                <a
                                    href={p.path}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-1 text-xs text-moebus-green hover:underline"
                                >
                                    Vorschau <ExternalLink size={12} />
                                </a>
                            </div>
                            <div className="flex items-center gap-2">
                                <HistoryButton
                                    historyKey={`page_${p.key}`}
                                    onRestore={(v) => setContents((prev) => ({ ...prev, [p.key]: v.content || "" }))}
                                />
                                <button
                                    data-testid={`save-legal-${p.key}`}
                                    onClick={() => savePage(p.key)}
                                    disabled={savingKey === p.key}
                                    className="inline-flex items-center gap-2 bg-moebus-green hover:bg-emerald-700 text-white font-medium px-4 py-2 rounded-md text-sm disabled:opacity-60"
                                >
                                    <Save size={14} /> {savingKey === p.key ? "Speichern…" : "Speichern"}
                                </button>
                            </div>
                        </div>
                        <textarea
                            data-testid={`legal-content-${p.key}`}
                            value={contents[p.key] || ""}
                            onChange={(e) => setContents({ ...contents, [p.key]: e.target.value })}
                            rows={18}
                            className="w-full px-3 py-2.5 border border-moebus-border rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-moebus-green resize-y leading-relaxed"
                        />
                    </div>
                ))}
        </div>
    );
};
